/**
 * ★ O CANO — `RTCPeerConnection`, data channel e a troca de SDP pelo NOSSO
 * servidor. Sexto irmão de `transporte-tempo-real.js`, extraído dele quando o
 * arquivo bateu no teto de 500 linhas ([spec-008 T036]).
 *
 * A divisão é por RESPONSABILIDADE, não por tamanho: aqui está **o que fala
 * WebRTC e HTTP**; lá ficou **a sessão** — o microfone (que é contrato de
 * ordem, N9), o estado, os verbos do contrato e o consumo cobrável. Quem quiser
 * entender a negociação lê este arquivo inteiro sem passar por nada de
 * política; quem quiser entender o ciclo de vida da sessão não tropeça em SDP.
 *
 * ## ICE "vanilla", e por que a espera existe
 *
 * O offer viaja numa requisição HTTP e **não há canal de sinalização de volta**
 * para candidatos avulsos — ou seja, o offer precisa sair completo. Por isso
 * `esperarColetaDeIce` vem ANTES do POST, com teto: estourado o teto, manda-se
 * o que já existe. Offer parcial costuma bastar; offer nenhum nunca conecta.
 *
 * ## Sem `iceServers`, de propósito
 *
 * É a configuração das 7 sessões medidas (`-protocolo.js`): o provedor tem
 * endereço público e o navegador o alcança com candidato `host`. Acrescentar
 * STUN/TURN aqui seria configuração não medida no caminho mais delicado.
 */
import { apiVoz } from '@modules/aawz-intelligence-v2/api/apiVoz'
import {
  ErroDeVozTempoReal,
  MOTIVO_SESSAO_RECUSADA,
  NOME_DO_CANAL_DE_EVENTOS,
  esperarColetaDeIce,
} from './transporte-tempo-real-protocolo'

/**
 * @param {Object} params
 * @param {(mensagem: Object) => void} params.aoReceberMensagem mensagem do data
 *   channel, crua — quem a interpreta é o `-eventos.js`
 * @param {(estado: string) => void} params.aoMudarConexao estado do transporte
 *   de mídia; quem decide o que é queda é o transporte
 * @param {(stream: MediaStream) => void} params.aoReceberTrilha a trilha remota
 *   de áudio. Este módulo não a toca: não há DOM aqui
 * @param {() => void} [params.aoAbrirCanal] ★ [T062] o data channel ficou
 *   `open`. É o ÚNICO instante em que dá para falar primeiro: `negociar`
 *   resolve quando o *answer* é aplicado, e o canal ainda leva um tempo para
 *   abrir — `enviar` antes disso devolve `false` em silêncio, e a saudação
 *   simplesmente não aconteceria. Opcional: quem não quer abrir a conversa
 *   não passa o gancho.
 */
export const criarCanalWebRtc = ({
  aoReceberMensagem,
  aoMudarConexao,
  aoReceberTrilha,
  aoAbrirCanal,
}) => {
  let _pc = null
  let _canal = null

  const ativo = () => Boolean(_pc)

  /**
   * Monta a conexão e publica a trilha do microfone. O data channel é criado
   * por NÓS (o provedor não o abre): o nome é o medido, `oai-events`.
   */
  const montar = (stream) => {
    const pc = new globalThis.RTCPeerConnection()
    stream.getTracks().forEach((track) => pc.addTrack(track, stream))
    const canal = pc.createDataChannel(NOME_DO_CANAL_DE_EVENTOS)
    canal.onmessage = aoReceberMensagem
    // ★ [T062] `onopen` é o "agora dá para falar". Só é instalado quando o
    // gancho existe — sem ele o canal segue byte a byte como antes.
    if (typeof aoAbrirCanal === 'function') canal.onopen = () => aoAbrirCanal()
    pc.onconnectionstatechange = () => aoMudarConexao(pc.connectionState)
    pc.ontrack = (evento) => {
      const fluxo = evento && evento.streams && evento.streams[0]
      if (fluxo) aoReceberTrilha(fluxo)
    }
    _pc = pc
    _canal = canal
  }

  /**
   * Gera o offer, espera a coleta de ICE, troca por um answer no NOSSO servidor
   * e o aplica. **Nenhuma chave passa por aqui** — N1 é estrutural: o navegador
   * recebe só o `sdp_answer` (D-1 corrigida por medição).
   *
   * @throws {ErroDeVozTempoReal} `MOTIVO_SESSAO_RECUSADA` — quem chama desmonta
   *   e decide a queda para `porTurnos`. O teardown é de lá porque o microfone
   *   também é de lá (N9).
   * @returns {Promise<{idSessao: string, modelo: string, tetoSegundos: number}>}
   */
  const negociar = async ({ superficie, idConversa }) => {
    const offer = await _pc.createOffer()
    await _pc.setLocalDescription(offer)
    await esperarColetaDeIce(_pc)
    const sdpOffer =
      (_pc.localDescription && _pc.localDescription.sdp) || offer.sdp
    const r = await apiVoz.abrirSessaoTempoReal({
      sdpOffer,
      superficie,
      idConversa,
    })
    if (!r || r.status !== 200 || !r.value) {
      throw new ErroDeVozTempoReal(
        MOTIVO_SESSAO_RECUSADA,
        (r && r.message) || 'Sessão de voz em tempo real recusada.'
      )
    }
    await _pc.setRemoteDescription({ type: 'answer', sdp: r.value.sdp_answer })
    return {
      idSessao: r.value.id_sessao,
      modelo: r.value.modelo,
      tetoSegundos: r.value.teto_segundos,
    }
  }

  /**
   * O ÚNICO ponto de saída para o fio. Best-effort por desenho: com o canal
   * caído não há a quem enviar, e o servidor encerra por TTL de qualquer forma.
   */
  const enviar = (mensagem) => {
    if (!_canal || _canal.readyState !== 'open') return false
    try {
      _canal.send(JSON.stringify(mensagem))
      return true
    } catch (erro) {
      return false // canal caiu entre a checagem e o envio.
    }
  }

  /** Idempotente: fechar o que já está fechado não é erro, é o caminho comum. */
  const fechar = () => {
    if (_canal) {
      try {
        _canal.close()
      } catch (erro) {
        // canal já fechado — nada a desfazer.
      }
      _canal = null
    }
    if (!_pc) return
    _pc.onconnectionstatechange = null
    try {
      _pc.close()
    } catch (erro) {
      // conexão já fechada — nada a desfazer.
    }
    _pc = null
  }

  return { ativo, montar, negociar, enviar, fechar }
}
